import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DeviceMobile, X, DownloadSimple, Export, PlusSquare } from "@phosphor-icons/react";
import { isStandalone, isIos, checkInstalled, getDeferredPrompt, clearDeferredPrompt, installInstructions, recordInstall } from "@/lib/installPrompt";

// Per-device nudge: shows every sign-in (session) until the app is installed on
// THIS device or the user ticks "Don't show this again". Installing on another
// device doesn't silence it here — detection is per-device by design.
const DISMISS_KEY = "saproject:install-prompt-dismissed";      // permanent (this device)
const SESSION_KEY = "saproject:install-prompt-snoozed";        // this session only

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(() => getDeferredPrompt());
  const [show, setShow] = useState(false);
  const [ios] = useState(() => isIos());
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    // Running as the installed app (covers iOS + installs done outside the prompt).
    if (isStandalone()) { recordInstall("standalone-launch"); return; }
    const onInstalled = () => recordInstall("browser");
    window.addEventListener("appinstalled", onInstalled);
    if (localStorage.getItem(DISMISS_KEY) || sessionStorage.getItem(SESSION_KEY)) {
      return () => window.removeEventListener("appinstalled", onInstalled);
    }
    let alive = true;
    checkInstalled().then((installed) => {
      if (!alive || installed) return;
      // Show after a beat — with the one-tap Install button when the browser offers
      // it, otherwise with per-browser "Add to Home Screen" steps.
      setTimeout(() => { if (alive) setShow(true); }, 2500);
    });
    const onPrompt = (e) => {
      e.preventDefault();
      if (alive) setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => {
      alive = false;
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    if (dontShow) localStorage.setItem(DISMISS_KEY, "1");
    else sessionStorage.setItem(SESSION_KEY, "1");
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem("saproject:pwa-installed", "1");
      localStorage.setItem(DISMISS_KEY, "1");
      recordInstall("prompt");
    }
    clearDeferredPrompt();
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-sm z-[80]"
          data-testid="install-prompt"
        >
          <div className="rounded-2xl border border-[#3F3F46] bg-[#0A0D14] text-white shadow-2xl p-4 flex gap-3 items-start">
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "rgba(204,255,0,0.15)" }}>
              <DeviceMobile size={22} color="#CCFF00" weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm mb-0.5 text-white">Install the Self-Agency app on this device</div>
              {ios ? (
                <div className="text-xs text-white/70 leading-relaxed" data-testid="install-ios-steps">
                  Tap <Export size={13} className="inline -mt-0.5" color="#CCFF00" weight="bold" /> <b>Share</b>, then{" "}
                  <PlusSquare size={13} className="inline -mt-0.5" color="#CCFF00" weight="bold" /> <b>Add to Home Screen</b> — full screen video, no browser bars.
                </div>
              ) : deferred ? (
                <div className="text-xs text-white/70 leading-relaxed">One tap to install — full screen video, no browser bars, right beside your other apps.</div>
              ) : (
                <div className="text-xs text-white/70 leading-relaxed" data-testid="install-manual-steps">{installInstructions()}</div>
              )}
              {!ios && deferred && (
                <button onClick={install} className="mt-2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full font-display font-bold text-xs" style={{ background: "#CCFF00", color: "#000000", border: "none", cursor: "pointer" }} data-testid="install-prompt-button">
                  <DownloadSimple size={14} weight="bold" /> Install
                </button>
              )}
              <a href="/install-guide" target="_blank" rel="noreferrer" data-testid="install-banner-guide-link"
                className="block mt-2 text-[11px] font-bold no-underline hover:underline" style={{ color: "#CCFF00" }}>
                Step-by-step guide for every device →
              </a>
              <label className="flex items-center gap-1.5 mt-2.5 text-[11px] text-white/60 cursor-pointer select-none">
                <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)}
                  className="accent-[#CCFF00] w-3.5 h-3.5" data-testid="install-prompt-dont-show" />
                Don't show this again on this device
              </label>
            </div>
            <button onClick={dismiss} className="text-white/50 hover:text-white bg-transparent border-0 cursor-pointer p-0.5" title="Close" data-testid="install-prompt-dismiss">
              <X size={16} weight="bold" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt;
